import { adminSupabase } from './supabaseService';
import { userService } from './userService';
import {
    CopyTradeRule,
    CopyTrade,
    CreateCopyTradeRuleRequest,
    UpdateCopyTradeRuleRequest,
    CopyTradeRuleStatistics
} from '../types/copyTrader';

export class CopyTradeService {
    /**
     * Get all copy trade rules for a user
     */
    async getCopyTradeRules(walletAddress: string): Promise<CopyTradeRule[]> {
        try {
            console.log(`📋 Loading copy trade rules for wallet: ${walletAddress}`);

            const { data: rules, error } = await adminSupabase
                .from('copy_trade_rules')
                .select('*')
                .eq('wallet_address', walletAddress)
                .order('created_at', { ascending: false });

            if (error) {
                console.error('❌ Error loading copy trade rules:', error);
                throw error;
            }

            console.log(`✅ Loaded ${rules?.length || 0} copy trade rules`);
            return rules || [];
        } catch (error) {
            console.error('❌ Failed to get copy trade rules:', error);
            throw error;
        }
    }

    /**
     * Create a new copy trade rule
     */
    async createCopyTradeRule(
        walletAddress: string,
        ruleData: CreateCopyTradeRuleRequest
    ): Promise<CopyTradeRule> {
        try {
            console.log(`➕ Creating copy trade rule:`, { walletAddress, ruleData });

            // Get user ID
            const user = await userService.getUserByWallet(walletAddress);
            if (!user) {
                throw new Error('User not found');
            }

            const newRule = {
                user_id: user.id,
                wallet_address: walletAddress,
                name: ruleData.name,
                enabled: false, // Always start disabled
                source_wallet_address: ruleData.source_wallet_address,
                source_wallet_name: ruleData.source_wallet_name,
                trading_wallet_addresses: ruleData.trading_wallet_addresses,
                copy_delay_seconds: ruleData.copy_delay_seconds || 2,
                trade_size_type: ruleData.trade_size_type || 'fixed',
                trade_size_value: ruleData.trade_size_value || '0.1',
                max_slippage_percent: ruleData.max_slippage_percent || '35',
                max_trades_to_copy: ruleData.max_trades_to_copy || 10,
                copy_buys: ruleData.copy_buys !== false,
                copy_sells: ruleData.copy_sells !== false,
                token_whitelist: ruleData.token_whitelist || [],
                token_blacklist: ruleData.token_blacklist || [],
                configuration_metadata: {}
            };

            const { data, error } = await adminSupabase
                .from('copy_trade_rules')
                .insert([newRule])
                .select()
                .single();

            if (error) {
                console.error('❌ Error creating copy trade rule:', error);
                throw error;
            }

            console.log(`✅ Created copy trade rule: ${data.name}`);
            return data;
        } catch (error) {
            console.error('❌ Failed to create copy trade rule:', error);
            throw error;
        }
    }

    /**
     * Update an existing copy trade rule
     */
    async updateCopyTradeRule(
        walletAddress: string,
        ruleId: string,
        updates: UpdateCopyTradeRuleRequest
    ): Promise<CopyTradeRule> {
        try {
            console.log(`🔄 Updating copy trade rule:`, { walletAddress, ruleId, updates });

            const { data, error } = await adminSupabase
                .from('copy_trade_rules')
                .update(updates)
                .eq('id', ruleId)
                .eq('wallet_address', walletAddress)
                .select()
                .single();

            if (error) {
                console.error('❌ Error updating copy trade rule:', error);
                throw error;
            }

            console.log(`✅ Updated copy trade rule: ${data.name}`);
            return data;
        } catch (error) {
            console.error('❌ Failed to update copy trade rule:', error);
            throw error;
        }
    }

    /**
     * Delete a copy trade rule
     */
    async deleteCopyTradeRule(walletAddress: string, ruleId: string): Promise<void> {
        try {
            console.log(`🗑️ Deleting copy trade rule:`, { walletAddress, ruleId });

            const { error } = await adminSupabase
                .from('copy_trade_rules')
                .delete()
                .eq('id', ruleId)
                .eq('wallet_address', walletAddress);

            if (error) {
                console.error('❌ Error deleting copy trade rule:', error);
                throw error;
            }

            console.log(`✅ Deleted copy trade rule`);
        } catch (error) {
            console.error('❌ Failed to delete copy trade rule:', error);
            throw error;
        }
    }

    /**
     * Toggle rule enabled/disabled state
     */
    async toggleCopyTradeRule(walletAddress: string, ruleId: string, enabled: boolean): Promise<CopyTradeRule> {
        try {
            console.log(`🔄 Toggling copy trade rule:`, { walletAddress, ruleId, enabled });

            const updateData: any = { enabled };

            // Update last_triggered_at when enabling
            if (enabled) {
                updateData.last_triggered_at = new Date().toISOString();
            }

            const { data, error } = await adminSupabase
                .from('copy_trade_rules')
                .update(updateData)
                .eq('id', ruleId)
                .eq('wallet_address', walletAddress)
                .select()
                .single();

            if (error) {
                console.error('❌ Error toggling copy trade rule:', error);
                throw error;
            }

            console.log(`✅ Toggled copy trade rule: ${data.name} -> ${enabled ? 'enabled' : 'disabled'}`);
            return data;
        } catch (error) {
            console.error('❌ Failed to toggle copy trade rule:', error);
            throw error;
        }
    }

    /**
     * Log a copy trade execution
     */
    async logCopyTrade(copyTradeData: {
        ruleId: string;
        ruleName: string;
        userWalletAddress: string;
        tradingWalletAddress: string;
        sourceWallet: string;
        sourceTransactionSignature?: string;
        sourceTransactionData?: any;
        transactionType: 'buy' | 'sell';
        tokenAddress: string;
        tokenSymbol?: string;
        tokenName?: string;
        amount: number;
        tokenAmount?: number;
        priceUsd?: number;
        slippagePercent?: number;
        copyDelayApplied: number;
        executionTimeMs?: number;
        transactionSignature?: string;
        status: 'pending' | 'success' | 'failed';
        errorMessage?: string;
        tradeContext?: any;
    }): Promise<CopyTrade> {
        try {
            console.log(`📝 Logging copy trade:`, {
                rule: copyTradeData.ruleName,
                token: copyTradeData.tokenSymbol,
                type: copyTradeData.transactionType,
                status: copyTradeData.status
            });

            // Get user ID
            const user = await userService.getUserByWallet(copyTradeData.userWalletAddress);
            if (!user) {
                throw new Error('User not found');
            }

            const copyTradeRecord = {
                user_id: user.id,
                wallet_address: copyTradeData.userWalletAddress,
                copy_rule_id: copyTradeData.ruleId, // Use new copy_rule_id field
                rule_name: copyTradeData.ruleName,
                source_wallet: copyTradeData.sourceWallet,
                source_transaction_signature: copyTradeData.sourceTransactionSignature,
                source_transaction_data: copyTradeData.sourceTransactionData || {},
                trading_wallet_address: copyTradeData.tradingWalletAddress,
                transaction_type: copyTradeData.transactionType,
                token_address: copyTradeData.tokenAddress,
                token_symbol: copyTradeData.tokenSymbol,
                token_name: copyTradeData.tokenName,
                amount: copyTradeData.amount,
                token_amount: copyTradeData.tokenAmount,
                price_usd: copyTradeData.priceUsd,
                slippage_percent: copyTradeData.slippagePercent,
                copy_delay_applied: copyTradeData.copyDelayApplied,
                execution_time_ms: copyTradeData.executionTimeMs,
                transaction_signature: copyTradeData.transactionSignature,
                status: copyTradeData.status,
                error_message: copyTradeData.errorMessage,
                trade_context: copyTradeData.tradeContext || {}
            };

            const { data, error } = await adminSupabase
                .from('copy_trades')
                .insert([copyTradeRecord])
                .select()
                .single();

            if (error) {
                console.error('❌ Error logging copy trade:', error);
                throw error;
            }

            // Update rule statistics
            await this.updateRuleStatistics(copyTradeData.ruleId, copyTradeData.status, copyTradeData.amount);

            console.log(`✅ Copy trade logged successfully`);
            return data;
        } catch (error) {
            console.error('❌ Failed to log copy trade:', error);
            throw error;
        }
    }

    /**
     * Update rule statistics after a trade
     */
    private async updateRuleStatistics(ruleId: string, status: 'pending' | 'success' | 'failed', amount: number): Promise<void> {
        try {
            if (status === 'pending') return; // Don't update stats for pending trades

            // First get current values
            const { data: currentRule, error: fetchError } = await adminSupabase
                .from('copy_trade_rules')
                .select('total_triggers, successful_trades, failed_trades, total_volume_sol')
                .eq('id', ruleId)
                .single();

            if (fetchError || !currentRule) {
                console.error('❌ Error fetching current rule stats:', fetchError);
                return;
            }

            // Calculate new values
            const updates: any = {
                total_triggers: currentRule.total_triggers + 1,
                total_volume_sol: currentRule.total_volume_sol + amount
            };

            if (status === 'success') {
                updates.successful_trades = currentRule.successful_trades + 1;
            } else if (status === 'failed') {
                updates.failed_trades = currentRule.failed_trades + 1;
            }

            const { error } = await adminSupabase
                .from('copy_trade_rules')
                .update(updates)
                .eq('id', ruleId);

            if (error) {
                console.error('❌ Error updating rule statistics:', error);
            }
        } catch (error) {
            console.error('❌ Failed to update rule statistics:', error);
        }
    }

    /**
     * Get copy trade history for a user
     */
    async getCopyTradeHistory(walletAddress: string, limit: number = 50): Promise<CopyTrade[]> {
        try {
            console.log(`📊 Loading copy trade history for wallet: ${walletAddress}`);

            const { data: trades, error } = await adminSupabase
                .from('copy_trades')
                .select('*')
                .eq('wallet_address', walletAddress)
                .order('created_at', { ascending: false })
                .limit(limit);

            if (error) {
                console.error('❌ Error loading copy trade history:', error);
                throw error;
            }

            console.log(`✅ Loaded ${trades?.length || 0} copy trades`);
            return trades || [];
        } catch (error) {
            console.error('❌ Failed to get copy trade history:', error);
            throw error;
        }
    }

    /**
     * Get rule statistics
     */
    async getRuleStatistics(walletAddress: string): Promise<Map<string, CopyTradeRuleStatistics>> {
        try {
            const { data: rules, error } = await adminSupabase
                .from('copy_trade_rules')
                .select('id, total_triggers, successful_trades, failed_trades, total_volume_sol, total_profit_loss_sol, last_triggered_at')
                .eq('wallet_address', walletAddress);

            if (error) {
                console.error('❌ Error loading rule statistics:', error);
                throw error;
            }

            const statsMap = new Map<string, CopyTradeRuleStatistics>();

            (rules || []).forEach(rule => {
                const successRate = rule.total_triggers > 0 ? (rule.successful_trades / rule.total_triggers) * 100 : 0;
                const averageTradeSize = rule.total_triggers > 0 ? rule.total_volume_sol / rule.total_triggers : 0;

                statsMap.set(rule.id, {
                    rule_id: rule.id,
                    total_trades: rule.total_triggers,
                    successful_trades: rule.successful_trades,
                    failed_trades: rule.failed_trades,
                    success_rate: successRate,
                    total_volume_sol: rule.total_volume_sol,
                    total_profit_loss_sol: rule.total_profit_loss_sol,
                    average_trade_size_sol: averageTradeSize,
                    last_trade_at: rule.last_triggered_at
                });
            });

            return statsMap;
        } catch (error) {
            console.error('❌ Failed to get rule statistics:', error);
            return new Map();
        }
    }

    /**
     * Subscribe to real-time copy trade updates
     */
    subscribeToTradeUpdates(walletAddress: string, callback: (trade: CopyTrade) => void) {
        console.log(`🔔 Subscribing to real-time copy trade updates for wallet: ${walletAddress}`);

        const subscription = adminSupabase
            .channel('copy_trades_realtime')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'copy_trades',
                    filter: `wallet_address=eq.${walletAddress}`
                },
                (payload) => {
                    console.log(`📨 Real-time copy trade update:`, payload);

                    if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
                        callback(payload.new as CopyTrade);
                    }
                }
            )
            .subscribe();

        return () => {
            console.log(`🔕 Unsubscribing from copy trade updates`);
            subscription.unsubscribe();
        };
    }

    /**
     * Get active copy trade rules (enabled rules)
     */
    async getActiveCopyTradeRules(walletAddress: string): Promise<CopyTradeRule[]> {
        try {
            const { data: rules, error } = await adminSupabase
                .from('copy_trade_rules')
                .select('*')
                .eq('wallet_address', walletAddress)
                .eq('enabled', true)
                .order('created_at', { ascending: false });

            if (error) {
                console.error('❌ Error loading active copy trade rules:', error);
                throw error;
            }

            return rules || [];
        } catch (error) {
            console.error('❌ Failed to get active copy trade rules:', error);
            throw error;
        }
    }
}

// Export singleton instance
export const copyTradeService = new CopyTradeService(); 